import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, DollarSign, Percent, Calendar, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FinancingSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [providerName, setProviderName] = useState('');
  const [options, setOptions] = useState([]);

  useEffect(() => {
    fetchDefaults();
  }, []);

  const fetchDefaults = async () => {
    try {
      const response = await axios.get(`${API}/settings/financing-defaults`);
      setProviderName(response.data.provider_name || '');
      setOptions(response.data.options || []);
    } catch (error) {
      console.error('Failed to fetch financing defaults:', error);
      toast.error('Failed to load financing settings');
    } finally {
      setLoading(false);
    }
  };

  const addOption = () => {
    setOptions([...options, {
      plan_name: `${(options.length + 1) * 12} Month Plan`,
      term_months: (options.length + 1) * 12,
      apr_percent: 0
    }]);
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const removeOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/financing-defaults`, {
        provider_name: providerName,
        options: options.map(opt => ({
          plan_name: opt.plan_name,
          term_months: parseInt(opt.term_months) || 12,
          apr_percent: parseFloat(opt.apr_percent) || 0
        }))
      });
      toast.success('Financing settings saved');
    } catch (error) {
      console.error('Failed to save financing settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const calculateMonthlyPayment = (principal, apr, months) => {
    if (!principal || !months) return 0;
    if (apr === 0) return principal / months;
    const monthlyRate = apr / 100 / 12;
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
           (Math.pow(1 + monthlyRate, months) - 1);
  };

  // Example payment based on $10,000 principal
  const examplePrincipal = 10000;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <DollarSign className="h-6 w-6 text-green-600" />
              Financing Settings
            </h1>
            <p className="text-muted-foreground">
              Configure default financing options for estimates
            </p>
          </div>
        </div>

        {/* Provider Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Financing Provider</CardTitle>
            <CardDescription>
              Name of your financing partner (shown to customers)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-md">
              <Label htmlFor="provider">Provider Name</Label>
              <Input
                id="provider"
                value={providerName}
                onChange={(e) => setProviderName(e.target.value)}
                placeholder="e.g., GreenSky, Synchrony, etc."
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Financing Options */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Default Financing Plans</CardTitle>
                <CardDescription>
                  These options will be available when adding financing to estimates
                </CardDescription>
              </div>
              <Button onClick={addOption} variant="outline" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Plan
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {options.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No financing plans configured. Click "Add Plan" to create one.
              </div>
            ) : (
              <div className="space-y-4">
                {options.map((option, index) => {
                  const monthlyPayment = calculateMonthlyPayment(
                    examplePrincipal, 
                    parseFloat(option.apr_percent) || 0, 
                    parseInt(option.term_months) || 12
                  );
                  
                  return (
                    <Card key={index} className="bg-slate-50 dark:bg-slate-800">
                      <CardContent className="pt-4">
                        <div className="grid md:grid-cols-4 gap-4 items-end">
                          <div className="md:col-span-2">
                            <Label>Plan Name</Label>
                            <Input
                              value={option.plan_name}
                              onChange={(e) => updateOption(index, 'plan_name', e.target.value)}
                              placeholder="e.g., 12 Month 0% APR"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Term (Months)
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              value={option.term_months}
                              onChange={(e) => updateOption(index, 'term_months', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="flex items-center gap-1">
                              <Percent className="h-3 w-3" />
                              APR %
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={option.apr_percent}
                              onChange={(e) => updateOption(index, 'apr_percent', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-4 pt-3 border-t">
                          <div className="text-sm text-muted-foreground">
                            Example: ${examplePrincipal.toLocaleString()} → 
                            <span className="font-semibold text-foreground ml-1">
                              ${monthlyPayment.toFixed(2)}/mo
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeOption(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default FinancingSettings;
